import {useCallback, useEffect} from 'react';

import {PageFilter} from 'filters/types';

import {useFilterDialogContext} from 'filters/features/FilterDialog/contexts/FilterDialogProvider/FilterDialogProvider';
import {neverCase} from 'utils/misc';
import {yup} from 'utils/yup';
import {useValidationContext} from 'filters/features/FilterDialog/contexts/internal/ValidationProvider/ValidationProvider';
import {getItemId} from 'filters/features/FilterDialog/utils/internal';

const rethrowValidationError = async (id: string, runner: () => Promise<void>) => {
  try {
    await runner();
  } catch (e) {
    if (e instanceof yup.ValidationError) {
      e.path = id;
    }

    throw e;
  }
};

export const useFilterDialogValidator = (filter: PageFilter | undefined, open: boolean) => {
  const {sections, warn: warnForm, validate: validateForm} = useFilterDialogContext();
  const {setFormError, setFormWarning, setFieldErrors} = useValidationContext();

  // For now we only support one field error at a time
  const validateFields = useCallback(
    async (filter: PageFilter) => {
      try {
        for (const section of sections) {
          switch (section.type) {
            case 'active-filters':
            case 'recent-filters':
            case 'saved-filters':
              break;

            case 'tree-view': {
              if (section.validate) {
                await rethrowValidationError(section.id, async () => {
                  await section.validate?.validate(section.getValue(filter));
                });
              }

              break;
            }

            case 'items': {
              for (const item of section.items) {
                if (!item.validate) {
                  continue;
                }

                await rethrowValidationError(getItemId(section.id, item.id), async () => {
                  await item.validate?.validate(item.getValue(filter), {
                    context: {
                      path: getItemId(section.id, item.id),
                    },
                  });
                });
              }

              break;
            }

            default:
              neverCase(section);
          }
        }

        return null;
      } catch (e) {
        if (e instanceof yup.ValidationError) {
          if (!e.path) {
            throw new Error('Path should be defined for yup validation errors');
          }

          return {
            [e.path]: e.message,
          };
        }
      }
    },
    [sections]
  );

  useEffect(() => {
    (async () => {
      if (!open || !filter) {
        return;
      }

      setFormError(null);
      setFormWarning(null);
      setFieldErrors({});

      const fieldErrors = await validateFields(filter);

      if (fieldErrors) {
        return setFieldErrors(fieldErrors);
      }

      if (validateForm) {
        const formError = await validateForm(filter);

        if (formError !== true) {
          return setFormError(formError);
        }
      }

      if (warnForm) {
        const formWarning = await warnForm(filter);

        if (formWarning !== true) {
          return setFormWarning(formWarning);
        }
      }
    })();
  }, [filter, open, setFieldErrors, setFormError, setFormWarning, validateFields, validateForm, warnForm]);
};
